import { lazy, Suspense, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { getCheckpointResponse, getQuizSetOverride, saveCheckpointResponse } from "../lib/firebase";
import { CHECKPOINT_SETS, NOTE_MAX, cleanAnswers, quizScore, type CheckpointSet } from "../lib/checkpoints";

type Props = {
  userId: string;
  courseId: string;
  setId: string;
  /** "start" hides itself for good once answered or skipped; "lesson" stays and shows a thank-you. */
  variant: "start" | "lesson";
};

const QuizEditor = lazy(() => import("./QuizEditor").then((m) => ({ default: m.QuizEditor })));

/** Private check-in card: a few multiple-choice questions + an optional note. Never blocks learning.
 *  A quiz set (set.quiz) instead shows the score, right answers and explanations after submitting. */
export function CheckpointCard({ userId, courseId, setId, variant }: Props) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  // The admin-edited version (quizSets/{setId}) wins over the default written in code.
  const [set, setSet] = useState<CheckpointSet | undefined>(CHECKPOINT_SETS[setId]);
  useEffect(() => {
    let alive = true;
    getQuizSetOverride(setId).then((o) => alive && o && setSet(o)).catch(() => {});
    return () => { alive = false; };
  }, [setId]);
  const [state, setState] = useState<"loading" | "open" | "done" | "hidden">("loading");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!set) return;
    let alive = true;
    getCheckpointResponse(userId, courseId, setId)
      .then((r) => {
        if (!alive) return;
        if (!r) return setState("open");
        if (variant === "start") return setState("hidden");
        setAnswers(r.answers);
        setNote(r.note);
        setState(r.skipped ? "hidden" : "done");
      })
      .catch(() => alive && setState("hidden")); // a check-in must never break the lesson page
    return () => { alive = false; };
  }, [userId, courseId, setId, variant, set]);

  if (!set || state === "loading" || state === "hidden") return null;
  const current = set; // narrowed for the save() closure below

  if (editing) {
    return (
      <Suspense fallback={null}>
        <QuizEditor set={set} onCancel={() => setEditing(false)} onSaved={(s) => { setSet(s); setEditing(false); }} />
      </Suspense>
    );
  }
  const editButton = isAdmin && set.quiz && (
    <button type="button" className="btn-ghost quiz-edit-btn" onClick={() => setEditing(true)}>✏️ แก้ Quiz</button>
  );

  async function save(skipped: boolean) {
    setSaving(true);
    setError(null);
    try {
      await saveCheckpointResponse({
        userId,
        courseId,
        setId,
        answers: skipped ? {} : cleanAnswers(current, answers),
        note: skipped ? "" : note.trim().slice(0, NOTE_MAX),
        skipped,
      });
      setState(skipped || variant === "start" ? "hidden" : "done");
    } catch {
      setError("บันทึกไม่สำเร็จ ลองอีกครั้งนะครับ");
    } finally {
      setSaving(false);
    }
  }

  if (state === "done" && set.quiz) {
    const score = quizScore(set, answers);
    return (
      <section className="checkpoint quiz-result" aria-label={set.title}>
        {editButton}
        <h2 className="checkpoint__title">{set.title}</h2>
        <p className="quiz-result__score">
          ได้ <strong>{score}</strong> จาก {set.questions.length} ข้อ
          {score === set.questions.length ? " เต็ม! เก่งมากครับ" : " ดูเฉลยด้านล่าง แล้วลองทำใหม่ได้เลย"}
        </p>
        <ol className="quiz-result__list">
          {set.questions.map((q) => {
            const right = answers[q.id] === q.answer;
            const correct = q.options.find((o) => o.id === q.answer)?.label;
            const mine = q.options.find((o) => o.id === answers[q.id])?.label;
            return (
              <li key={q.id} className={right ? "is-right" : "is-wrong"}>
                <strong>{right ? "✓" : "✗"} {q.text}</strong>
                {!right && mine && <span className="quiz-result__mine">คุณตอบ: {mine}</span>}
                <span className="quiz-result__answer">เฉลย: {correct}</span>
                {q.explain && <span className="quiz-result__why">{q.explain}</span>}
              </li>
            );
          })}
        </ol>
        <button type="button" className="btn-secondary" onClick={() => { setAnswers({}); setState("open"); }}>ทำใหม่</button>
      </section>
    );
  }

  if (state === "done") {
    return (
      <section className="checkpoint checkpoint--done" aria-label={set.title}>
        <p>ขอบคุณที่ตอบครับ คำตอบนี้ช่วยให้ดิวปรับคอร์สได้ตรงขึ้น</p>
        <button type="button" className="btn-ghost" onClick={() => setState("open")}>แก้คำตอบ</button>
      </section>
    );
  }

  const answeredAll = set.questions.every((q) => answers[q.id]);

  return (
    <section className="checkpoint" aria-label={set.title}>
      {editButton}
      <h2 className="checkpoint__title">{set.title}</h2>
      <p className="checkpoint__intro">{set.intro}</p>
      {set.questions.map((q, i) => (
        <fieldset key={q.id} className="checkpoint__q">
          <legend>{set.quiz ? `${i + 1}. ` : ""}{q.text}</legend>
          {q.options.map((o) => (
            <label key={o.id} className={answers[q.id] === o.id ? "checkpoint__opt is-on" : "checkpoint__opt"}>
              <input
                type="radio"
                name={`${setId}-${q.id}`}
                value={o.id}
                checked={answers[q.id] === o.id}
                onChange={() => setAnswers({ ...answers, [q.id]: o.id })}
              />
              {o.label}
            </label>
          ))}
        </fieldset>
      ))}
      {set.noteLabel && (
        <label className="checkpoint__note">
          {set.noteLabel} <small>(ไม่บังคับ)</small>
          <textarea value={note} maxLength={NOTE_MAX} rows={3} onChange={(e) => setNote(e.target.value)} />
        </label>
      )}
      {error && <p className="error">{error}</p>}
      <div className="checkpoint__actions">
        <button type="button" onClick={() => save(false)} disabled={saving || !answeredAll}>
          {saving ? "กำลังบันทึก…" : set.quiz ? "ส่งคำตอบ ดูคะแนน" : "ส่งคำตอบ"}
        </button>
        {!set.quiz && (
          <button type="button" className="btn-ghost" onClick={() => save(true)} disabled={saving}>
            ข้ามไปก่อน
          </button>
        )}
      </div>
    </section>
  );
}
